import { AspectRatioName, DataResponse, Dimensions } from 'services/types';

export type FontSource = 'upload' | 'google';

export type SupportRequestType =
  | 'podcastSearchNoResult'
  | 'selfServePublisherRequestError';

export type PersonalFont = {
  /**
   * human friendly display name
   */
  displayName: string;
  /**
   * internal id string used to specify font
   */
  familyName: string;
  /**
   * font family name with fvd (https://github.com/typekit/fvd)
   */
  familyNameWithVariations: string;
  /**
   * font id
   */
  fontId: number;
  /**
   * enum for describing if the font was uploaded or associated through google
   */
  fontSource?: FontSource;
  /**
   * url to font css file
   */
  url: string;
  /**
   * fvd (font variation description: https://github.com/typekit/fvd)
   */
  variations: string[];
};

export type PersonalFontsResponse = {
  personalFonts: PersonalFont[];
};

export type GoogleFont = {
  /**
   * google font family name
   */
  family: string;
  /**
   * css url to the load the font for preview. note that this css only supports
   * characters that exist in the family name. i.e. you can't use this css url to
   * load any arbitrary text, you can only use it to preview the family name text.
   */
  previewCssUrl: string;
};

export type GoogleFontsResponse = {
  items: GoogleFont[];
};

export type SupportRequestAttribute = {
  /**
   * used only with podcastSearchNoResult type.search term that was used which
   * led to no result found
   */
  searchTerm?: string;
  /**
   * used only with selfServePublisherRequestError type.rss url that was entered
   */
  rssUrl?: string;
  /**
   * used only with selfServePublisherRequestError type.website url that was entered
   */
  websiteUrl?: string;
};

export type CreateSupportRequestArgs = {
  requestType: SupportRequestType;
};

export type UpdateFontNameArgs = {
  /**
   * human friendly display name
   */
  displayName: string;
};

export type BaseAdjustmentConfig<TName, TValue> = {
  /**
   * the shape of the response here changes based on the valueType field. See
   * specific models for reference:#stringConfig, #integerConfig, #booleanConfig
   */
  valueType: TName;
  default?: TValue;
  hide?: boolean;
  readonly?: boolean;
  overrideOptions?: TValue[];
  showOptions: TValue[];
  hideOptions: TValue[];
};

export type StringAdjustmentConfig = BaseAdjustmentConfig<'string', string>;

export type IntegerAdjustmentConfig = BaseAdjustmentConfig<'integer', number>;

export type BooleanAdjustmentConfig = BaseAdjustmentConfig<'boolean', boolean>;

export type ObjectAdjustmentConfig<TObject extends object = object> =
  BaseAdjustmentConfig<'object', TObject>;

export type EnabledAdjustmentConfig = {
  enabled?: BooleanAdjustmentConfig;
};

export type HiddenAdjustmentConfig = {
  hidden?: BooleanAdjustmentConfig;
};

export type AssetsAdjustmentConfig = {
  assets?: StringAdjustmentConfig;
};

export type AudioAdjustmentConfig = {
  selectorMethods?: StringAdjustmentConfig;
  transcription?: EnabledAdjustmentConfig;
};

export type WaveformAdjustmentConfig = {
  color?: StringAdjustmentConfig;
  generation?: StringAdjustmentConfig;
  position?: StringAdjustmentConfig;
  type?: StringAdjustmentConfig;
  userPreference?: EnabledAdjustmentConfig;
};

export type TransitionAdjustmentConfig = {
  durationMilli?: IntegerAdjustmentConfig;
  effect?: StringAdjustmentConfig;
};

export type TextAdjustmentConfig = {
  inTransition: TransitionAdjustmentConfig;
  outTransitoin: TransitionAdjustmentConfig;
  presetStyle?: StringAdjustmentConfig;
};

export type WatermarkAdjustmentConfig = StringAdjustmentConfig;

type AspectRatioAdjustmentConfig<TValue> = Partial<
  Record<AspectRatioName, TValue>
>;

export type WatermarkOptionsAdjustmentConfig =
  AspectRatioAdjustmentConfig<ObjectAdjustmentConfig>;

export type VideoExportAdjustmentConfig = {
  /**
   * @deprecated
   */
  quality?: IntegerAdjustmentConfig;
  /**
   * for internal use
   */
  frameRate?: IntegerAdjustmentConfig;
  /**
   * for internal use
   */
  sizeTypeId?: IntegerAdjustmentConfig;
  /**
   * for internal use
   */
  fullEpisodeFrameRate?: IntegerAdjustmentConfig;
  /**
   * for internal use
   */
  fullEpisodeSizeTypeId?: IntegerAdjustmentConfig;
  /**
   * @deprecated
   */
  size?: AspectRatioAdjustmentConfig<StringAdjustmentConfig>;
  /**
   * @deprecated
   */
  fullEpisodeSize?: AspectRatioAdjustmentConfig<StringAdjustmentConfig>;
  introVideo?: AspectRatioAdjustmentConfig<StringAdjustmentConfig>;
  outroVideo?: AspectRatioAdjustmentConfig<StringAdjustmentConfig>;
};

export type MediaSearchEngineAdjustmentConfig = {
  provider?: Partial<Record<'gif' | 'image' | 'video', StringAdjustmentConfig>>;
};

export type NavigationAdjustmentConfig = {
  automation?: HiddenAdjustmentConfig;
  bar?: HiddenAdjustmentConfig;
  headerLogo?: EnabledAdjustmentConfig;
};

export type TemplateAdjustmentConfig = {
  selection?: StringAdjustmentConfig;
};

export type VideoShareAdjustmentConfig = {
  shareMethod?: StringAdjustmentConfig;
};

export type EddyNavigationAdjustmentConfig = {
  target?: StringAdjustmentConfig;
};

export type EddyFeaturesAdjustmentConfig = Partial<
  Record<
    | 'clipSuggestions'
    | 'autoProjectName'
    | 'showProjectNameSuggestions'
    | 'showNotesSuggestions'
    | 'removeFillerWords'
    | 'podcastPersonalization',
    EnabledAdjustmentConfig
  >
>;

export type EddyAdjustmentConfig = {
  buttonColor?: StringAdjustmentConfig;
  export?: AssetsAdjustmentConfig;
  expressExport?: AssetsAdjustmentConfig;
  favicon?: StringAdjustmentConfig;
  features?: EddyFeaturesAdjustmentConfig;
  helpButton?: EnabledAdjustmentConfig;
  logo?: StringAdjustmentConfig;
  navigation?: EddyNavigationAdjustmentConfig;
  tabTitle?: StringAdjustmentConfig;
};

export type DisplayPreferences = {
  audio: AudioAdjustmentConfig;
  eddy: EddyAdjustmentConfig;
  mediaSearchEngine: MediaSearchEngineAdjustmentConfig;
  navigation: NavigationAdjustmentConfig;
  template: TemplateAdjustmentConfig;
  text: TextAdjustmentConfig;
  videoExport: VideoExportAdjustmentConfig;
  videoShare: VideoShareAdjustmentConfig;
  watermark?: WatermarkAdjustmentConfig;
  watermarkOptions?: WatermarkOptionsAdjustmentConfig;
  waveform: WaveformAdjustmentConfig;
};

export type DisplayPreferencesResponse = {
  displayAdjustment: DisplayPreferences;
};

/**
 * This type is generic because the API doesnt' specify a type but rather leaves
 * it to the individual applications
 */
export type WaveformUserPreferences<TWaveformConfig extends object = object> = {
  dimensionHeight: number;
  dimensionWidth: number;
  waveformConfig: TWaveformConfig;
};

export type CaptionUserPreferences = {
  /**
   * @default true
   */
  captionEnabled: boolean;
  /**
   * @default en-US
   */
  captionLanguage: string;
};

export type VideoExportUserPreferences = {
  frameQuality?: IntegerAdjustmentConfig;
  frameRate?: IntegerAdjustmentConfig;
  frameSize?: StringAdjustmentConfig;
  fullEpisodeFrameRate?: IntegerAdjustmentConfig;
  fullEpisodeFrameSize?: StringAdjustmentConfig;
};

export type VideoExportFrameSizeName = 'standard' | 'hd' | 'fullHd';

export type VideoExportUserPreferencesUpdateRequest = {
  frameRate: number;
  frameSize: VideoExportFrameSizeName;
  fullEpisodeFrameRate: number;
  fullEpisodeFrameSize: VideoExportFrameSizeName;
};

export type EddyUserPreferences = Partial<
  Record<
    | 'isAutoChaptersEnabled'
    | 'isAutoProjectNameEnabled'
    | 'shouldRemoveFillerWords'
    | 'isPodcastPersonalizationEnabled',
    BooleanAdjustmentConfig
  >
>;

type ColorPrefs = string[];

export type UserPreferences = {
  waveformPrefs?: WaveformUserPreferences[];
  colorPrefs: ColorPrefs;
  captionPrefs: CaptionUserPreferences;
  /**
   * video export preferences. these can be set either by user preferences or overriden
   * with organization/integrator display preferences. for the actual video export sizes,
   * use the endpoint /user-pref/video-sizes
   */
  videoExportPrefs: VideoExportUserPreferences;
  /**
   * eddy preferences. these can be set either by user preferences or overriden
   * with organization/integrator display preferences.
   */
  eddyPrefs: EddyUserPreferences;
};

export type UpdateColorPreferencesRequest = Pick<UserPreferences, 'colorPrefs'>;

export type SampleAudioGenre = {
  id: number;
  name: string;
};

export type SampleAudio = {
  id: number;
  audioUrl: string;
  /**
   * title of the audio file to be displayed to the user
   */
  title: string;
  durationMillis: number;
  genre?: SampleAudioGenre[];
};

export type SampleAudioResponse = {
  sampleAudios: SampleAudio[];
};

export type AnnouncementRuntime = {
  /**
   * if null, this announcement does not expire
   */
  endMillis?: number;
  /**
   * if null, assume beginning of time
   */
  startMillis?: number;
};

export type AnnouncementPart = {
  bannerIcon?: string;
  bannerText?: string;
  titleText?: string;
  subtitleText?: string;
  mainHtml?: string;
};

export type Announcement = {
  id: number;
  announcementParts?: AnnouncementPart[];
  validBetween?: AnnouncementRuntime;
  /**
   * once the user has viewed the announcement (what counts as "viewed" is up to
   * the client), the client should make a GET request to this url
   */
  viewConfirmationUrl: string;
};

export type AnnouncementsResponse = {
  announcements: Announcement[];
};

/**
 * This type is generic because the API doesnt' specify a type but rather leaves
 * it to the individual applications
 */
export type OnboardingItem<TName extends string = string> = {
  id: number;
  name?: TName;
  /**
   * was this onboarding tutorial viewed by the user
   */
  isViewed?: boolean;
  /**
   * once the user has viewed the onboarding tutorial (what counts as "viewed" is
   * up to the client), the client should make a GET request to this url
   */
  viewConfirmationUrl: string;
};

export type TemplateType = 'headlinerDefault' | 'userGenerated';

export type MakeVideoType = 'shortClip' | 'fullEpisode';

export type TemplatePreviewVideo = {
  videoUrl: string;
  mediaType: string;
};

export type TemplateExportPlatform =
  | 'instagram'
  | 'instagramStory'
  | 'facebook'
  | 'twitter'
  | 'youtube';

export type ProjectTemplateStatus =
  | 'queued'
  | 'processing'
  | 'completed'
  | 'error'
  | 'errorAck';

export type ProjectTemplateThumbnail = {
  url?: string;
  status?: ProjectTemplateStatus;
};

export type ProjectTemplate = {
  /**
   * if set to true, the caller of the api has access to delete the template
   */
  canDelete: boolean;
  /**
   * list of compatible video types for this template
   */
  compatibleVideoTypes?: MakeVideoType[];
  configurationId: string;
  description: string;
  dimension?: Dimensions;
  displayName: string;
  /**
   * list of expor & At platforms that this template is associated with
   */
  exportPlatforms: TemplateExportPlatform[];
  id: string;
  isDeleted: boolean;
  name: string;
  /**
   * list of preview videos in the order of preference. it is up the client to
   * pick and choose which video touse.
   */
  previewVideos: TemplatePreviewVideo[];
  /**
   * thumbnail generated for the template based on the first frame
   */
  templateThumbnail: ProjectTemplateThumbnail;
  thumbnailImageUrl: string;
  type: MakeVideoType;
};

export type ProjectTemplatesResponse = {
  projectTemplates: ProjectTemplate[];
};

export type CreateProjectTemplateRequest = {
  /**
   * also known as the widget id
   */
  configurationId: string;
  /**
   * name of the template as it should be displayed to the user
   */
  displayName: string;
  /**
   * Flag to tell backend if we should insert mock audio when generating a preview.
   * This is mainly for displaying the waveform if soundwave field is set. In some
   * scenarios, the user expects the soundwave to show up in the preview image even
   * when there's no audio attached to the configuration.
   *
   * @default false
   */
  useMockAudioForPreview?: boolean;
};

export type ProjectEditorCompatibility<TDetails = undefined> = {
  isCompatible: boolean;
} & (TDetails extends undefined
  ? Record<string, never>
  : { compatibilityDetails: TDetails });

export type FullEpisodeTemplateCompatibilityDetails = {
  validAnimationSettings: boolean;
  validAssetStartAndEndTimes: boolean;
  validCaptionsSettings: boolean;
  validVideoLayer: boolean;
};

export type FullEpisodeTemplateCompatibility =
  ProjectEditorCompatibility<FullEpisodeTemplateCompatibilityDetails>;

export type ProjectTemplateCompatibility = {
  fullEpisode: FullEpisodeTemplateCompatibility;
  ucsEditor: ProjectEditorCompatibility;
};

export enum QuestionIdentifier {
  PODCAST = 'podcastYesNo',
  ROLE = 'tellUsWhatYouDo',
  REFERRER = 'referrer',
}

export type QuestionResponse = {
  questionIdentifier: QuestionIdentifier;
  response: string;
};

export type Questionnaire = {
  questionResponses?: QuestionResponse[];
};

export type QuestionnaireStatusType = 'incomplete' | 'completed';

export type QuestionnaireStatus = {
  status: QuestionnaireStatusType;
  questionsWithNoResponse?: QuestionResponse[];
};

export type VideoSizeModel = {
  id: number;
  name: string;
} & Record<AspectRatioName, Dimensions>;

export type VideoSizesResponse = {
  data?: VideoSizeModel[];
};

export type Language = {
  name: string;
  languageCode: string;
};

export type LanguagesResponse = DataResponse<Language[]>;

export type CreateWaveformPreferenceResponse = {
  waveformPrefId: number;
};

export type ExperimentVariantVariables = {
  /**
   * name of Swagger model schema for the experiment
   */
  experimentConfigSchema: string;
};

export type Experiment = {
  experimentId: number;
  experimentName: string;
  variantId: number;
  variantName: string;
  variantWeight: number;
  /**
   * variant specific variables. look at #experiment[number]Configuration model
   * for the expected response of each experiment
   */
  variantVariables?: ExperimentVariantVariables;
};

/**
 * This type is generic because the API doesnt' specify a type but rather leaves
 * it to the individual applications
 */
export type TextPreset<
  TAdvTextConfig extends object = object,
  TAdvAnimConfig extends object = object,
> = {
  /**
   * text config for advanced visuals. these fields should map to the embed config's
   * advancedTextConfigs field
   */
  advancedTextConfigs?: TAdvTextConfig;
  /**
   * text config for advanced animation. these fields should map to the embed config's
   * advancedAnimation field
   */
  advancedAnimation?: TAdvAnimConfig;
  /**
   * flag to determine if user can delete the preset
   */
  canDelete: boolean;
  /**
   * map of css rules. javascript naming convention (camel case) for keys.
   */
  cssStyles: object;
  /**
   * expected to be populated for Headliner defined presets. expected to be empty for user stored presets
   */
  previewImageUrl?: string;
  textPresetId: string;
};

export type GetTextPresetsResponse = {
  textPresets: TextPreset[];
};

export type CreateMyTextPresetArgs = {
  /**
   * map of css rules. javascript naming convention (camel case) for keys.
   */
  cssStyles: object;
  /**
   * text config for advanced visuals. these fields should map to the embed config's
   * advancedTextConfigs field.
   */
  advancedTextConfigs?: object;
  /**
   * text config for advanced animation. these fields should map to the embed config's
   * advancedAnimation field
   */
  advancedAnimation?: object;
};

export type CreateTextPresetResponse = {
  textPresetId: string;
};

export type NotificationAlert = {
  /**
   * flag for if user has new social share data
   */
  hasNewSocialShareData?: boolean;
};
